import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowRight, Save, Send, FileText, CreditCard, Banknote, CheckCircle, X, AlertTriangle } from 'lucide-react'
import useInvoiceStore from '../../stores/useInvoiceStore'
import useCustomerStore from '../../stores/useCustomerStore'
import usePermissions from '../../hooks/usePermissions'
import CustomerSelector from '../../components/invoices/CustomerSelector'
import ProductSelector from '../../components/invoices/ProductSelector'
import LineItemRow from '../../components/invoices/LineItemRow'
import AddCustomerModal from '../../components/invoices/AddCustomerModal'

/**
 * Create/Edit Invoice Page
 * Full invoice creation and editing form
 */
const CreateInvoice = () => {
  const navigate = useNavigate()
  const { id } = useParams() // Get invoice ID for edit mode
  const isEditMode = !!id
  const permissions = usePermissions()

  const {
    selectedCustomer,
    selectedInvoice,
    lineItems,
    notes,
    subtotal,
    tax,
    taxRate,
    total,
    isLoading,
    error,
    setNotes,
    setCustomer,
    saveInvoice,
    updateInvoice,
    getInvoiceById,
    loadInvoiceForEdit,
    clearError,
    paymentMethods,
    fetchPaymentMethods,
    fetchTaxRate
  } = useInvoiceStore()

  const { fetchCustomers } = useCustomerStore()

  // Discount state
  const [discountAmount, setDiscountAmount] = useState(0)
  const [discountType, setDiscountType] = useState('fixed') // 'fixed' or 'percentage'

  // Load invoice data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      getInvoiceById(id).then((invoice) => {
        if (invoice && loadInvoiceForEdit) {
          loadInvoiceForEdit(invoice)

          // Set payment method from loaded invoice
          if (invoice.status) {
            setPaymentMethod(invoice.status) // 'cash' or 'credit'
          }

          // Set selected payment method if it's a cash invoice
          if (invoice.payment_method) {
            setSelectedPaymentMethod(invoice.payment_method)
          }

          // Set discount data from loaded invoice
          if (invoice.discount_value > 0) {
            setDiscountType(invoice.discount_type || 'fixed')
            setDiscountAmount(invoice.discount_value || 0)
          }
        }
      })
    }
  }, [isEditMode, id])

  // Refresh customer data when page loads to get updated credit limits
  useEffect(() => {
    fetchCustomers()
  }, [])

  // Fetch payment methods and tax rate on mount
  useEffect(() => {
    if (paymentMethods.length === 0) {
      fetchPaymentMethods()
    }
    fetchTaxRate() // Fetch tax rate from settings
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [isSaving, setIsSaving] = useState(false)
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('cash') // 'cash' or 'credit'
  // REMOVED: paidAmount, initialPaymentMethod - NEW SYSTEM: receipts are independent
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null) // For cash invoices only
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showCreditLimitWarning, setShowCreditLimitWarning] = useState(false)
  const [creditLimitMessage, setCreditLimitMessage] = useState('')
  // Save invoice data before reset for success modal
  const [savedInvoiceData, setSavedInvoiceData] = useState(null)

  const handleCustomerAdded = (newCustomer) => {
    setCustomer(newCustomer)
  }

  const handleSaveDraft = async () => {
    setIsSaving(true)

    // Save current invoice data before reset
    const invoiceSnapshot = {
      customer: selectedCustomer,
      subtotal,
      discount: {
        type: discountType,
        amount: discountAmount,
        value: discountType === 'percentage'
          ? (subtotal * discountAmount / 100)
          : discountAmount
      },
      taxRate,
      tax: (() => {
        const discount = discountType === 'percentage'
          ? (subtotal * discountAmount / 100)
          : discountAmount
        return (subtotal - discount) * taxRate
      })(),
      total: (() => {
        const discount = discountType === 'percentage'
          ? (subtotal * discountAmount / 100)
          : discountAmount
        const afterDiscount = subtotal - discount
        return afterDiscount + (afterDiscount * taxRate)
      })(),
      paymentMethod
    }

    const result = await saveInvoice(true)
    setIsSaving(false)

    if (result.success) {
      setSavedInvoiceData(invoiceSnapshot)
      setShowSuccessModal(true)
      // Don't auto-navigate - let user close modal manually
    }
  }

  const handleSubmit = async () => {
    // NEW SYSTEM (December 2025): Simplified credit limit check
    // - Cash invoices: Full payment, no credit check needed
    // - Credit invoices: Check if customer can take more credit

    if (!isEditMode && selectedCustomer && paymentMethod === 'credit') {
      const creditLimit = selectedCustomer.credit_limit || selectedCustomer.creditLimit || 0

      // Only check if customer has a credit limit defined
      if (creditLimit > 0) {
        const currentBalance = parseFloat(selectedCustomer.current_balance || selectedCustomer.currentBalance || 0)
        const newInvoiceAmount = parseFloat(total)

        // Check if customer is already over limit
        if (currentBalance > parseFloat(creditLimit)) {
          const overAmount = currentBalance - creditLimit

          setCreditLimitMessage(
            `🚫 العميل ${selectedCustomer.name} متجاوز الحد الائتماني حالياً!\n\n` +
            `📊 الحد الائتماني: ${creditLimit.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك.\n` +
            `💰 الرصيد الحالي المستحق: ${currentBalance.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك.\n` +
            `📈 متجاوز الحد بمقدار: ${overAmount.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك.\n\n` +
            `❌ لا يمكن إضافة فواتير آجلة جديدة للعميل حتى يتم سداد المتأخرات\n\n` +
            `✅ الخيارات المتاحة:\n` +
            `   • تحويل الفاتورة إلى كاش (دفع فوري كامل)\n` +
            `   • تحصيل سند قبض من العميل أولاً ثم إنشاء الفاتورة`
          )
          setShowCreditLimitWarning(true)
          return
        }

        // Check if new invoice will exceed credit limit
        const futureBalance = currentBalance + newInvoiceAmount
        if (futureBalance > creditLimit) {
          const excessAmount = futureBalance - creditLimit

          setCreditLimitMessage(
            `⚠️ تحذير: الحد الائتماني للعميل ${selectedCustomer.name}\n\n` +
            `📊 الحد الائتماني: ${creditLimit.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك.\n` +
            `💰 الرصيد الحالي: ${currentBalance.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك.\n` +
            `🧾 مبلغ الفاتورة الجديدة: ${newInvoiceAmount.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك.\n\n` +
            `❌ الرصيد المتوقع: ${futureBalance.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك.\n` +
            `📈 سيتجاوز الحد بمقدار: ${excessAmount.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك.\n\n` +
            `✅ يجب تحويل الفاتورة إلى كاش (دفع فوري) أو تحصيل سند قبض أولاً`
          )
          setShowCreditLimitWarning(true)
          return
        }
      }
    }

    setIsSaving(true)

    // Save current invoice data before reset
    const invoiceSnapshot = {
      customer: selectedCustomer,
      subtotal,
      discount: {
        type: discountType,
        amount: discountAmount,
        value: discountType === 'percentage'
          ? (subtotal * discountAmount / 100)
          : discountAmount
      },
      taxRate,
      tax: (() => {
        const discount = discountType === 'percentage'
          ? (subtotal * discountAmount / 100)
          : discountAmount
        return (subtotal - discount) * taxRate
      })(),
      total: (() => {
        const discount = discountType === 'percentage'
          ? (subtotal * discountAmount / 100)
          : discountAmount
        const afterDiscount = subtotal - discount
        return afterDiscount + (afterDiscount * taxRate)
      })(),
      paymentMethod
    }

    let result
    if (isEditMode) {
      // Update existing invoice with discount and payment data
      result = await updateInvoice(id, {
        selectedCustomer,
        lineItems,
        notes,
        subtotal,
        tax,
        total,
        discountType,
        discountAmount,
        paymentMethod,
        selectedPaymentMethod
      })
    } else {
      // NEW SYSTEM: Create invoice with payment method and discount
      const paymentData = {
        method: paymentMethod,
        payment_method_id: paymentMethod === 'cash' && selectedPaymentMethod ? selectedPaymentMethod.id : null
      }

      const discountData = {
        type: discountType,
        value: discountAmount
      }

      result = await saveInvoice(false, paymentData, discountData)
    }

    setIsSaving(false)

    if (result.success) {
      setSavedInvoiceData(invoiceSnapshot)
      setShowSuccessModal(true)
      // Don't auto-navigate - let user close modal manually
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-6">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/invoices')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowRight className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {isEditMode ? 'تعديل الفاتورة' : 'فاتورة جديدة'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isEditMode
                ? `رقم الفاتورة: ${selectedInvoice?.invoiceNumber || id}`
                : 'رقم الفاتورة: سيتم إنشاؤه تلقائياً'
              }
            </p>
          </div>
        </div>
      </header>

      {/* Error Message */}
      {error && (
        <div className="mx-4 mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Form Content */}
      <div className="px-4 space-y-4">
        {/* Customer Section */}
        <div className="card p-4">
          <CustomerSelector onAddNew={() => setShowAddCustomerModal(true)} />
        </div>

        {/* Products Section */}
        <div className="card p-4">
          <ProductSelector />
        </div>

        {/* Line Items Table */}
        {lineItems.length > 0 && (
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              المنتجات المضافة
            </h3>

            {/* Table Header - Hidden on mobile, shown on larger screens */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <div className="col-span-5">المنتج</div>
              <div className="col-span-2">الكمية</div>
              <div className="col-span-2">السعر</div>
              <div className="col-span-2 text-left">الإجمالي</div>
              <div className="col-span-1"></div>
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              {lineItems.map((item) => (
                <LineItemRow key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* Totals Section */}
        {lineItems.length > 0 && (
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              الحسابات
            </h3>

            <div className="space-y-2">
              {/* Subtotal */}
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-700 dark:text-gray-300">الإجمالي الفرعي:</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {subtotal.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                </span>
              </div>

              {/* Discount - Only if user has permission */}
              {(() => {
                console.log('🔍 [CreateInvoice] Discount permission:', permissions.canAddDiscount)
                return null
              })()}
              {permissions.canAddDiscount && (
                <div className="py-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 dark:text-gray-300">الخصم:</span>
                    <div className="flex items-center gap-2">
                      {/* Discount Type Toggle */}
                      <select
                        value={discountType}
                        onChange={(e) => {
                          setDiscountType(e.target.value)
                          setDiscountAmount(0)
                        }}
                        className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="fixed">مبلغ ثابت</option>
                        <option value="percentage">نسبة مئوية</option>
                      </select>

                      {/* Discount Amount Input */}
                      <input
                        type="number"
                        min="0"
                        max={discountType === 'percentage' ? 100 : subtotal}
                        step={discountType === 'percentage' ? 1 : 0.001}
                        value={discountAmount}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0
                          if (discountType === 'percentage' && value > 100) {
                            setDiscountAmount(100)
                          } else if (discountType === 'fixed' && value > subtotal) {
                            setDiscountAmount(subtotal)
                          } else {
                            setDiscountAmount(value)
                          }
                        }}
                        className="w-24 px-2 py-1 text-sm text-right rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder="0"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {discountType === 'percentage' ? '%' : 'د.ك'}
                      </span>
                    </div>
                  </div>

                  {/* Calculated Discount Amount */}
                  {discountAmount > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-red-600 dark:text-red-400">قيمة الخصم:</span>
                      <span className="font-semibold text-red-600 dark:text-red-400">
                        -{(discountType === 'percentage'
                          ? (subtotal * discountAmount / 100)
                          : discountAmount
                        ).toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Tax */}
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-700 dark:text-gray-300">
                  ضريبة القيمة المضافة ({Math.round(taxRate * 100)}%):
                </span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {(() => {
                    const discount = discountType === 'percentage'
                      ? (subtotal * discountAmount / 100)
                      : discountAmount
                    const totalAfterDiscount = subtotal - discount
                    const taxAmount = totalAfterDiscount * taxRate
                    return taxAmount.toLocaleString('ar-KW', { minimumFractionDigits: 3 })
                  })()} د.ك
                </span>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

              {/* Total */}
              <div className="flex items-center justify-between py-2">
                <span className="text-lg font-semibold text-gray-900 dark:text-white">الإجمالي النهائي:</span>
                <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                  {(() => {
                    const discount = discountType === 'percentage'
                      ? (subtotal * discountAmount / 100)
                      : discountAmount
                    const totalAfterDiscount = subtotal - discount
                    const taxAmount = totalAfterDiscount * taxRate
                    return (totalAfterDiscount + taxAmount).toLocaleString('ar-KW', { minimumFractionDigits: 3 })
                  })()} د.ك
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Payment Method Section - NEW SYSTEM (December 2025) */}
        {lineItems.length > 0 && (
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              نوع الفاتورة
            </h3>

            {/* Payment Method Selection */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                type="button"
                onClick={() => setPaymentMethod('cash')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  paymentMethod === 'cash'
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-primary-300'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <Banknote className={`w-8 h-8 ${paymentMethod === 'cash' ? 'text-primary-600' : 'text-gray-500'}`} />
                  <span className={`font-semibold ${paymentMethod === 'cash' ? 'text-primary-600' : 'text-gray-700 dark:text-gray-300'}`}>
                    كاش
                  </span>
                  <span className="text-xs text-gray-500">دفع فوري كامل</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setPaymentMethod('credit')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  paymentMethod === 'credit'
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-primary-300'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <CreditCard className={`w-8 h-8 ${paymentMethod === 'credit' ? 'text-primary-600' : 'text-gray-500'}`} />
                  <span className={`font-semibold ${paymentMethod === 'credit' ? 'text-primary-600' : 'text-gray-700 dark:text-gray-300'}`}>
                    آجل
                  </span>
                  <span className="text-xs text-gray-500">بدون دفع الآن</span>
                </div>
              </button>
            </div>

            {/* Cash Payment Info */}
            {paymentMethod === 'cash' && (
              <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-800 dark:text-green-200 mb-1">
                      فاتورة كاش - دفع فوري
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300">
                      • المبلغ المدفوع: {total.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك (كامل المبلغ)
                    </p>
             
                  </div>
                </div>

                {/* Payment Method Selector for Cash */}
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    طريقة الدفع
                  </label>
                  <select
                    value={selectedPaymentMethod?.id || ''}
                    onChange={(e) => {
                      const method = paymentMethods.find(m => m.id == e.target.value)
                      setSelectedPaymentMethod(method)
                    }}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">اختر طريقة الدفع</option>
                    {paymentMethods.length > 0 ? (
                      paymentMethods.map((method) => (
                        <option key={method.id} value={method.id}>
                          {method.name} ({method.name_e})
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="3">نقدي (Cash)</option>
                        <option value="1">تحويل بنكي (Bank Transfer)</option>
                        <option value="2">شيك (Cheque)</option>
                      </>
                    )}
                  </select>
                </div>
              </div>
            )}

            {/* Credit Payment Info */}
            {paymentMethod === 'credit' && (
              <div>
                {/* Credit Limit Info */}
                {selectedCustomer && (selectedCustomer.credit_limit || selectedCustomer.creditLimit) > 0 && (
                  <div className="mb-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 text-xs space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">الحد الائتماني:</span>
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {(selectedCustomer.credit_limit || selectedCustomer.creditLimit).toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">الرصيد الحالي:</span>
                          <span className={`font-semibold ${
                            (selectedCustomer.current_balance || selectedCustomer.currentBalance || 0) > (selectedCustomer.credit_limit || selectedCustomer.creditLimit)
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-green-600 dark:text-green-400'
                          }`}>
                            {(selectedCustomer.current_balance || selectedCustomer.currentBalance || 0).toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-1 border-t border-blue-200 dark:border-blue-800">
                          <span className="text-gray-600 dark:text-gray-400">المتاح للائتمان:</span>
                          <span className={`font-bold ${
                            ((selectedCustomer.credit_limit || selectedCustomer.creditLimit) - (selectedCustomer.current_balance || selectedCustomer.currentBalance || 0)) > 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {Math.max(0, (selectedCustomer.credit_limit || selectedCustomer.creditLimit) - (selectedCustomer.current_balance || selectedCustomer.currentBalance || 0)).toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-orange-800 dark:text-orange-200 mb-1">
                        فاتورة آجلة - بدون دفع الآن
                      </p>
                      <p className="text-xs text-orange-700 dark:text-orange-300">
                        • المبلغ المستحق: {total.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                      </p>
                      <p className="text-xs text-orange-700 dark:text-orange-300">
                        • سيتم إضافتها لرصيد العميل
                      </p>
                      <p className="text-xs text-orange-700 dark:text-orange-300">
                        • يمكن إنشاء سند قبض لاحقاً من صفحة الفواتير
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes Section */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            ملاحظات
          </h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="أضف ملاحظات إضافية للفاتورة..."
            rows="3"
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3 pb-safe-bottom">
          <button
            onClick={handleSaveDraft}
            disabled={isSaving || isLoading || !selectedCustomer || lineItems.length === 0}
            className="btn btn-secondary flex items-center justify-center gap-2 touch-target disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            <span>حفظ مسودة</span>
          </button>

          <button
            onClick={handleSubmit}
            disabled={isSaving || isLoading || !selectedCustomer || lineItems.length === 0}
            className="btn btn-primary flex items-center justify-center gap-2 touch-target disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving || isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>جاري الحفظ...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>{isEditMode ? 'حفظ التعديلات' : 'إنشاء الفاتورة'}</span>
              </>
            )}
          </button>
        </div>

        {/* Empty State */}
        {lineItems.length === 0 && selectedCustomer && (
          <div className="card p-8 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              ابدأ بإضافة منتجات
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              استخدم حقل البحث أعلاه لإضافة منتجات للفاتورة
            </p>
          </div>
        )}
      </div>

      {/* Add Customer Modal */}
      <AddCustomerModal
        isOpen={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onCustomerAdded={handleCustomerAdded}
      />

      {/* Credit Limit Warning Modal */}
      {showCreditLimitWarning && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-lg w-full shadow-2xl transform animate-scaleIn border-4 border-red-500">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-400 animate-pulse" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-center text-red-600 dark:text-red-400 mb-4">
              تجاوز الحد الائتماني!
            </h2>

            {/* Message */}
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 mb-6">
              <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-sans text-right leading-relaxed">
                {creditLimitMessage}
              </pre>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowCreditLimitWarning(false)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-bold transition-colors shadow-lg"
              >
                فهمت، سأعدل المبلغ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-2xl transform animate-scaleIn relative">
            {/* Close Button (X) */}
            <button
              onClick={() => {
                setShowSuccessModal(false)
                setSavedInvoiceData(null)
                navigate('/invoices')
              }}
              className="absolute top-4 left-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="إغلاق"
            >
              <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-3">
              {isEditMode ? 'تم تعديل الفاتورة بنجاح!' : 'تم إنشاء الفاتورة بنجاح!'}
            </h2>

            {/* Message */}
            <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
              {isEditMode ? 'تم تحديث الفاتورة بنجاح' : 'تم حفظ الفاتورة وخصم المخزون من السيارة'}
            </p>

            {/* Details */}
            {savedInvoiceData && (
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-2 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">العميل:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{savedInvoiceData.customer?.name}</span>
                </div>

                {/* Subtotal */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">الإجمالي الفرعي:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {savedInvoiceData.subtotal.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                  </span>
                </div>

                {/* Discount - Only show if there is a discount */}
                {savedInvoiceData.discount.value > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      الخصم ({savedInvoiceData.discount.type === 'percentage' ? `${savedInvoiceData.discount.amount}%` : 'مبلغ ثابت'}):
                    </span>
                    <span className="font-semibold text-red-600 dark:text-red-400">
                      -{savedInvoiceData.discount.value.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                    </span>
                  </div>
                )}

                {/* Tax */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    ضريبة القيمة المضافة ({Math.round(savedInvoiceData.taxRate * 100)}%):
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {savedInvoiceData.tax.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                  </span>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-2"></div>

                {/* Total */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700 dark:text-gray-300 font-semibold">المبلغ الإجمالي:</span>
                  <span className="font-bold text-lg text-primary-600 dark:text-primary-400">
                    {savedInvoiceData.total.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                  </span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">نوع الفاتورة:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {savedInvoiceData.paymentMethod === 'cash' ? 'كاش (دفع فوري كامل)' : 'آجل (بدون دفع)'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">الحالة:</span>
                  <span className={`font-semibold ${savedInvoiceData.paymentMethod === 'cash' ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                    {savedInvoiceData.paymentMethod === 'cash' ? 'مدفوع بالكامل' : 'مستحق الدفع'}
                  </span>
                </div>
              </div>
            )}

            {/* Action Button */}
            <button
              onClick={() => {
                setShowSuccessModal(false)
                setSavedInvoiceData(null)
                navigate('/invoices')
              }}
              className="w-full btn btn-primary py-3 text-lg font-semibold"
            >
              موافق
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreateInvoice
